import { ChangeDetectionStrategy, Component, OnInit, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { SwiperModule } from 'swiper/angular';
import { environment } from 'src/environments/environment';
import { ApiService } from 'src/app/core/services/api.service';
import { Observable, combineLatest, map, startWith, tap } from 'rxjs';
import { IxfiCurrencyService } from 'header';
import { WebsocketService } from 'src/app/core/services/websocket.service';
import { FiatValuePipe } from '../../pipes/fiat-value.pipe';
import { getPrecision } from 'src/app/core/functions/utility';
import { ImageComponent } from '../image/image.component';
import { MarqueeCoin } from 'src/app/core/models/http.model';

@Component({
    selector: 'app-marquee',
    standalone: true,
    imports: [CommonModule, SwiperModule, FiatValuePipe, ImageComponent],
    template: `
        <div class="marquee" *ngIf="marqueeObs | async">
            <div class="marquee-content">
                <div class="coin-group" *ngFor="let item of marqueeCoins; let i = index; trackBy: trackByFn">
                    <div class="coin-icon">
                        <app-image
                            [imgWidth]="28"
                            [imgHeight]="28"
                            [imgStyle]="{ 'border-radius': '50px' }"
                            [imgSrc]="'coins/' + item.code + '.png'">
                        </app-image>
                    </div>
                    <div>
                        <div class="coin-name d-flex">
                            <strong> {{ item.name }} </strong>
                            <span>- {{ item.code | uppercase }}</span>
                        </div>
                        <div class="price">
                            {{
                                item.price
                                    | fiatValue : usdExchangeRate
                                    | currency : currencySymbol : 'symbol' : '1.0-' + item.precision
                            }}
                        </div>
                    </div>
                </div>
                <div class="coin-group" *ngFor="let item of marqueeCoins; let i = index; trackBy: trackByFn">
                    <div class="coin-icon">
                        <app-image
                            [imgWidth]="28"
                            [imgHeight]="28"
                            [imgStyle]="{ 'border-radius': '50px' }"
                            [imgSrc]="'coins/' + item.code + '.png'">
                        </app-image>
                    </div>
                    <div>
                        <div class="coin-name d-flex">
                            <strong> {{ item.name }} </strong>
                            <span>- {{ item.code | uppercase }}</span>
                        </div>
                        <div class="price">
                            {{
                                item.price
                                    | fiatValue : usdExchangeRate
                                    | currency : currencySymbol : 'symbol' : '1.0-' + item.precision
                            }}
                        </div>
                    </div>
                </div>
            </div>
        </div>
    `,
    styleUrls: ['./marquee.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class MarqueeComponent implements OnInit {
    CDN_URL = environment.CDN_URL;
    marqueeCoins: MarqueeCoin[] = [];

    currencySymbol: string = 'USD';
    usdExchangeRate: number = 1;

    marqueeObs!: Observable<boolean>;
    /* Services */
    private apiService = inject(ApiService);
    private websocketService = inject(WebsocketService);
    private currencyService = inject(IxfiCurrencyService);

    ngOnInit(): void {
        const marqueeCoinsHttp$: Observable<MarqueeCoin[]> = this.apiService.fetch<MarqueeCoin[]>('MARQUEE_COINS').pipe(
            map(response => {
                response = response.map(item => ({ ...item, precision: getPrecision(item.price) }));
                return response.sort((a, b) => (+a.priority < +b.priority ? -1 : 1));
            }),
            tap(response => (this.marqueeCoins = response))
        );

        const selectedCurrency$ = this.currencyService.currencyObs.pipe(
            map(response => response.selectedCurrency),
            tap(selectedCurrency => {
                this.currencySymbol = selectedCurrency.currency_symbol.toUpperCase();
                this.usdExchangeRate = this.currencySymbol === 'USD' ? 1 : selectedCurrency.usd_exchange_rate;
            })
        );

        const socket$ = this.websocketService.coinInfoObs.pipe(
            startWith(null),
            tap(response => {
                if (response) {
                    const index: number = this.marqueeCoins.findIndex(coin => coin.coin_id === response.coin_id);
                    if (index !== -1) {
                        this.marqueeCoins[index].price = response.coin_price_usd;
                        this.marqueeCoins[index].precision = getPrecision(this.marqueeCoins[index].price);
                    }
                }
            })
        );

        this.marqueeObs = combineLatest([selectedCurrency$, marqueeCoinsHttp$, socket$]).pipe(map(_ => true));
    }

    trackByFn(index: number, item: MarqueeCoin) {
        return item.coin_id;
    }
}
